import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Card as MuiCard,
  Checkbox,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import GroupsIcon from '@mui/icons-material/Groups'
import GridViewIcon from '@mui/icons-material/GridView'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import HomeIcon from '@mui/icons-material/Home'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import SportsScoreIcon from '@mui/icons-material/SportsScore'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { TestCreateSectionHeader } from '@/components/tests/TestCreateSectionHeader'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { lessonsApi } from '@/api/lessons'
import { tagsApi, type Tag } from '@/api/tagsApi'
import { testsApi, type TestPreviewRunRequestPayload } from '@/api/tests'
import { isLessonRecipientGroup, isLessonRecipientUser } from '@/utils/lessonRecipient'
import { getEnums, type EnumOption } from '@/api/enums'
import { getWordAttributeFilterKeys, type WordAttributeFilterKey } from '@/constants/wordAttributeFilterKeys'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'
import {
  ALL_SKILL_TYPES,
  ANSWER_FORMAT_MODES,
  type AnswerFormatMode,
  answerFormatModesFromGranular,
  answerTypeSupportsAutoChecking,
  deriveActiveSkillsFromLessonTestSelection,
  granularAnswerTypesFromModes,
  isSpeechAnswerType,
  modeHasCompatibleGranular,
  PROMPT_AUDIO,
  PROMPT_SOURCE_TEXT,
  PROMPT_TRANSLATION_TEXT,
  unionAnswerTypesForPromptPool,
} from '@/utils/testPromptAnswerMatrix'
import type { Lesson, LessonParticipant, TestTemplate } from '@/types'
import type { Card, WordAttribute } from '@/types/cards'
import { cardsApi } from '@/api/cardsApi'
import { useAuthStore } from '@/stores/authStore'
import '@/styles/dashboard.css'

type PreviewThreshold = {
  id: number
  name: string
  progress_from: number
  progress_to: number
  /** Сколько карточек теста должно приходиться на порог (абсолютное число после расчёта на бэкенде). */
  count_cards_in_test: number
}

/** Карточка у ученика с тем же словом, что у карточки учителя; при отсутствии — создаётся по слову учителя. */
async function ensureStudentCardForWordFromTeacherCard(teacherCard: Card, studentUserId: number): Promise<number> {
  const text = (teacherCard.word?.text ?? '').trim()
  if (!text) throw new Error('Нет текста слова')
  const listing = await cardsApi.getCards({ user_id: studentUserId, search: text, per_page: 30 })
  const lower = text.toLowerCase()
  const hit = listing.data.find((c) => (c.word?.text ?? '').trim().toLowerCase() === lower)
  if (hit) return hit.id
  const attrs = (teacherCard.word?.attributes ?? [])
    .filter((a: WordAttribute) => Boolean(a.key) && String(a.value ?? '').trim() !== '')
    .map((a: WordAttribute) => ({ key: String(a.key), value: String(a.value ?? '').trim() }))
  const created = await cardsApi.createCard({
    text,
    user_id: studentUserId,
    ...(attrs.length > 0 ? { attributes: attrs } : {}),
  })
  return created.id
}

type PreviewCard = {
  id: number
  text: string
  translation: string
  relax_filters: string[]
  threshold_id: number | null
  hit_threshold: boolean
  prompt_type: string | null
  answer_type: string | null
  skill_type: string[]
  /** Квотный навык из превью (для создания теста 1:1 с preview). */
  primary_skill_type?: string | null
}

type PreviewUser = {
  user_id: number
  count_need: number
  count_found: number
  shortage: number
  threshold: PreviewThreshold[]
  cards: PreviewCard[]
}

type PreviewState = { users: PreviewUser[] }

type PreviewCardWithUser = PreviewCard & { __user_id: number }

/** Задержка перед автообновлением предпросмотра после изменения формы (мс). */
const PREVIEW_DEBOUNCE_MS = 3_000

/** Макс. число точек в навигаторе учеников группы (при большем числе учеников — равномерная разбивка). */
const PREVIEW_GROUP_MAX_DOTS = 9

function previewGroupDotTargetIndices(studentCount: number, maxDots: number): number[] {
  if (studentCount <= 0) return []
  if (studentCount <= maxDots) return Array.from({ length: studentCount }, (_, i) => i)
  const last = maxDots - 1
  if (last <= 0) return [0]
  return Array.from({ length: maxDots }, (_, j) => Math.round((j * (studentCount - 1)) / last))
}

function closestPreviewGroupDotIndex(currentStudentIndex: number, dotTargets: number[]): number {
  if (dotTargets.length === 0) return 0
  let best = 0
  let bestDist = Infinity
  dotTargets.forEach((idx, j) => {
    const d = Math.abs(idx - currentStudentIndex)
    if (d < bestDist) {
      bestDist = d
      best = j
    }
  })
  return best
}

function pickDisplayName(p: LessonParticipant): string {
  return p.user?.display_name || p.user?.nickname || p.user?.email || `#${p.user_id}`
}

/** В теме `MuiCard` задан внешний `padding`; с `CardContent` получается двойной отступ — обнуляем на корне карточки. */
const LESSON_TEST_SECTION_CARD_SX: SxProps<Theme> = {
  scrollMarginTop: 16,
  p: 0,
}

/** Padding секций формы — как у панели предпросмотра (`Paper` с `p: 2`), без увеличенного `pb` у последнего `CardContent`. */
const LESSON_TEST_SECTION_CARD_CONTENT_SX: SxProps<Theme> = {
  p: 2,
  '&:last-child': { pb: 2 },
}

// formatPreviewCardSourceLine больше не показываем на карточках — пороги выводятся списком в PreviewPanel.

type Mode = 'classic' | 'timed' | 'series'
type Skill = 'translation' | 'reverse_translation' | 'spelling' | 'listening' | 'pronunciation'

type PromptTypeKey = typeof PROMPT_SOURCE_TEXT | typeof PROMPT_TRANSLATION_TEXT | typeof PROMPT_AUDIO

const PROMPT_TYPE_OPTIONS: Array<{ id: PromptTypeKey }> = [
  { id: PROMPT_SOURCE_TEXT },
  { id: PROMPT_TRANSLATION_TEXT },
  { id: PROMPT_AUDIO },
]

const SKILLS: Array<{ id: Skill; title: string; desc: string }> = [
  { id: 'translation', title: 'Перевод', desc: 'слово → перевести' },
  { id: 'reverse_translation', title: 'Обратный перевод', desc: 'перевод → слово' },
  { id: 'spelling', title: 'Правописание', desc: 'напечатать оригинал' },
  { id: 'listening', title: 'Восприятие на слух', desc: 'аудио → ответ' },
  { id: 'pronunciation', title: 'Произношение', desc: 'произнести вслух' },
]

type TemplateId = number | 'none'

const MODES: Array<{ id: Mode; title: string; desc: string }> = [
  { id: 'classic', title: 'Классический', desc: 'завершение по словам' },
  { id: 'timed', title: 'На время', desc: 'лимит минут' },
  { id: 'series', title: 'Серия', desc: 'N верных подряд' },
]

function modeTestHintAlertSx(theme: Theme) {
  return {
    py: 0.5,
    px: 1,
    alignItems: 'flex-start' as const,
    bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'light' ? 0.09 : 0.12),
    borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'light' ? 0.28 : 0.4),
    '& .MuiAlert-icon': {
      fontSize: '1.1rem',
      mr: 0.75,
      py: 0,
      alignSelf: 'flex-start',
      opacity: 0.9,
    },
    '& .MuiAlert-message': {
      fontSize: '0.75rem',
      lineHeight: 1.45,
      py: 0,
    },
  }
}

type AttributeOperator = 'has' | 'has_not' | 'eq' | 'ne' | 'contains' | 'not_contains'

type AttributeConditionRow = {
  id: string
  field: WordAttributeFilterKey
  operator: AttributeOperator
  value: string
}

type AttributeInputType = 'boolean' | 'enum' | 'text'

const ATTRIBUTE_META: Record<
  WordAttributeFilterKey,
  { label: string; inputType: AttributeInputType; enumKey?: 'PartOfSpeech' | 'WordType' }
> = {
  transcription:     { label: 'Транскрипция',         inputType: 'boolean' },
  translation:       { label: 'Перевод',               inputType: 'boolean' },
  audio_path:        { label: 'Аудиофайл',             inputType: 'boolean' },
  type:              { label: 'Тип слова',             inputType: 'enum',    enumKey: 'WordType' },
  part_of_speech:    { label: 'Часть речи',            inputType: 'enum',    enumKey: 'PartOfSpeech' },
  example_sentence:  { label: 'Пример предложения',   inputType: 'boolean' },
  synonym:           { label: 'Синоним',               inputType: 'boolean' },
  antonym:           { label: 'Антоним',               inputType: 'boolean' },
  note:              { label: 'Заметка',               inputType: 'text' },
}

const OPERATORS_BY_TYPE: Record<AttributeInputType, Array<{ value: AttributeOperator; label: string }>> = {
  boolean: [
    { value: 'has',     label: 'есть' },
    { value: 'has_not', label: 'нет' },
  ],
  enum: [
    { value: 'eq', label: 'равно' },
    { value: 'ne', label: 'не равно' },
  ],
  text: [
    { value: 'contains',     label: 'содержит' },
    { value: 'not_contains', label: 'не содержит' },
    { value: 'eq',           label: 'равно' },
    { value: 'ne',           label: 'не равно' },
  ],
}

function defaultOperator(field: WordAttributeFilterKey): AttributeOperator {
  const t = ATTRIBUTE_META[field].inputType
  return OPERATORS_BY_TYPE[t][0].value
}

function newAttributeConditionRow(): AttributeConditionRow {
  return {
    id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    field: 'part_of_speech',
    operator: 'eq',
    value: '',
  }
}

// teacher-preview теперь строится сервером через /api/tests/preview

function promptTypeHuman(pt: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return t(`tests.run.promptTypes.${pt}`, { defaultValue: pt })
}

function answerTypeShortLabel(at: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return t(`tests.run.answerTypeLabels.${at}`, { defaultValue: at })
}

function skillHuman(skill: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return t(`tests.run.skillLabels.${skill}`, { defaultValue: skill })
}

/** API должен отдавать string[]; на всякий случай — строка или объект из прокси. */
function normalizePreviewSkillTypes(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((s) => String(s).trim()).filter(Boolean)
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return []
}

function CardBlock({
  card,
  isRelaxed,
  skillBadges: _skillBadges,
  autoCheckEnabled,
  thresholdColors,
}: {
  card: PreviewCard
  isRelaxed: boolean
  skillBadges: Skill[]
  autoCheckEnabled: boolean
  thresholdColors?: { bg: string; border: string; chipBg: string } | null
}) {
  const { t } = useTranslation()
  const ctxPrompt = card.prompt_type?.trim() || null
  const ctxAnswer = card.answer_type?.trim() || null
  const evaluatedSkills = useMemo(() => normalizePreviewSkillTypes(card.skill_type), [card.skill_type])
  const relaxCaption = useMemo(() => {
    if (!isRelaxed) return null
    const rf = card.relax_filters ?? []
    const hasTags = rf.includes('tags')
    const hasFilters = rf.includes('filters')
    if (hasTags && hasFilters) return t('tests.run.preview.relaxTagsAndFilters')
    if (hasTags) return t('tests.run.preview.relaxTags')
    if (hasFilters) return t('tests.run.preview.relaxFilters')
    return t('tests.run.preview.relaxGeneric')
  }, [isRelaxed, card.relax_filters, t])
  /** Жёлтый фон — только если послабление и карточка не в интервале порога; иначе как «по фильтрам» + подпись послабления. */
  const useWarningChrome = isRelaxed && !card.hit_threshold
  const showAutoBadge = Boolean(autoCheckEnabled && ctxAnswer && answerTypeSupportsAutoChecking(ctxAnswer))

  const denseChipSx: SxProps<Theme> = {
    height: 24,
    maxWidth: 180,
    backgroundImage: 'none',
    '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis' },
    '& .MuiChip-icon': { ml: 0.5 },
  }

  const badgeBg = thresholdColors?.chipBg ?? null

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        px: 1.25,
        py: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        bgcolor: useWarningChrome
          ? alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.06 : 0.12)
          : thresholdColors?.bg ?? 'background.paper',
        borderColor: useWarningChrome
          ? alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.35 : 0.4)
          : thresholdColors?.border ?? 'divider',
      })}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
          {card.text || `Карточка #${card.id}`}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          title={card.translation || undefined}
          sx={{ display: 'block', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
        >
          {card.translation || '—'}
        </Typography>
        {ctxPrompt || ctxAnswer || evaluatedSkills.length > 0 ? (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              mt: 0.5,
              alignItems: 'center',
              flexWrap: 'wrap',
              rowGap: 0.5,
              overflow: 'visible',
              '& > *': { flexShrink: 0 },
            }}
          >
            {ctxPrompt ? (
              <Tooltip title={t('tests.run.badges.tooltipDisplay', { value: promptTypeHuman(ctxPrompt, t) })}>
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                  label={promptTypeHuman(ctxPrompt, t)}
                  sx={(theme) => ({
                    ...denseChipSx,
                    backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important`,
                    '&.MuiChip-root': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                    '&.MuiChip-outlined': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                  })}
                />
              </Tooltip>
            ) : null}

            {evaluatedSkills.map((sk, idx) => (
              <Tooltip key={`${sk}-${idx}`} title={t('tests.run.badges.tooltipSkill', { value: skillHuman(sk, t) })}>
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<TrackChangesIcon sx={{ fontSize: 16 }} />}
                  label={skillHuman(sk, t)}
                  sx={(theme) => ({
                    ...denseChipSx,
                    backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important`,
                    '&.MuiChip-root': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                    '&.MuiChip-outlined': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                  })}
                />
              </Tooltip>
            ))}

            {ctxAnswer ? (
              <Tooltip title={t('tests.run.badges.tooltipAnswer', { value: answerTypeShortLabel(ctxAnswer, t) })}>
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
                  label={answerTypeShortLabel(ctxAnswer, t)}
                  sx={(theme) => ({
                    ...denseChipSx,
                    backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important`,
                    '&.MuiChip-root': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                    '&.MuiChip-outlined': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                  })}
                />
              </Tooltip>
            ) : null}

            {showAutoBadge ? (
              <Tooltip title={t('tests.run.badges.autoTooltip')}>
                <Chip
                  size="small"
                  label={t('tests.run.badges.auto')}
                  color="success"
                  variant="outlined"
                  sx={{
                    height: 24,
                    backgroundColor: (theme) => `${badgeBg ?? theme.palette.background.paper} !important`,
                    backgroundImage: 'none',
                    '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' },
                    '&.MuiChip-root': { backgroundColor: (theme) => `${badgeBg ?? theme.palette.background.paper} !important` },
                    '&.MuiChip-outlined': { backgroundColor: (theme) => `${badgeBg ?? theme.palette.background.paper} !important` },
                  }}
                />
              </Tooltip>
            ) : null}
          </Stack>
        ) : null}
        {relaxCaption ? (
          <Typography
            variant="caption"
            color={useWarningChrome ? 'warning.main' : 'text.secondary'}
            sx={{ fontStyle: 'italic', display: 'block', mt: 0.25 }}
          >
            {relaxCaption}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  )
}

function PreviewStatsSkeleton() {
  return (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
      <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
        <Skeleton variant="text" width={48} height={36} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="70%" height={18} />
      </Paper>
      <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
        <Skeleton variant="text" width={36} height={36} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="65%" height={18} />
      </Paper>
    </Stack>
  )
}

function PreviewCardSkeleton() {
  return (
    <Paper variant="outlined" sx={{ p: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="55%" height={22} />
        <Skeleton variant="text" width="40%" height={18} />
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, pt: 0.25 }}>
        <Skeleton variant="rounded" width={36} height={24} />
        <Skeleton variant="rounded" width={36} height={24} />
      </Stack>
    </Paper>
  )
}

function hashToHue(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0
  }
  return h % 360
}

function pastelForKey(key: string): { bg: string; border: string; chipBg: string } {
  const hue = hashToHue(key)
  // Пастельные тона: высокая светлота, невысокая насыщенность.
  return {
    bg: `hsl(${hue} 65% 92%)`,
    border: `hsl(${hue} 50% 70%)`,
    // Подложка бейджей: чуть темнее, чем фон карточки.
    chipBg: `hsl(${hue} 60% 86%)`,
  }
}

function GroupPreviewStudentStrip(props: {
  users: PreviewUser[]
  participants: LessonParticipant[]
  selectedIndex: number
  onSelectIndex: (index: number) => void
}) {
  const { users, participants, selectedIndex, onSelectIndex } = props
  const n = users.length
  const dotTargets = useMemo(() => previewGroupDotTargetIndices(n, PREVIEW_GROUP_MAX_DOTS), [n])
  const activeDot = closestPreviewGroupDotIndex(selectedIndex, dotTargets)
  const uid = users[selectedIndex]?.user_id
  const participant = uid != null ? participants.find((p) => p.user_id === uid) : null
  const label = participant ? pickDisplayName(participant) : uid != null ? `#${uid}` : '—'

  const goPrev = () => onSelectIndex(Math.max(0, selectedIndex - 1))
  const goNext = () => onSelectIndex(Math.min(n - 1, selectedIndex + 1))

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 1.5,
        mb: 2,
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.04 : 0.08),
        borderColor: alpha(theme.palette.primary.main, 0.2),
      })}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <IconButton
            size="small"
            aria-label="Предыдущий ученик"
            onClick={goPrev}
            disabled={selectedIndex <= 0}
            sx={{ flexShrink: 0 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Avatar
            src={participant?.user?.avatar_url ?? undefined}
            sx={(theme) => ({
              width: 40,
              height: 40,
              flexShrink: 0,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.22),
              color: 'primary.main',
              fontWeight: 700,
            })}
          >
            {label.slice(0, 1).toUpperCase()}
          </Avatar>
          <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              Ученик в группе
            </Typography>
            <Typography variant="body2" fontWeight={600} noWrap title={label}>
              {label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedIndex + 1} из {n}
            </Typography>
          </Stack>
          <IconButton
            size="small"
            aria-label="Следующий ученик"
            onClick={goNext}
            disabled={selectedIndex >= n - 1}
            sx={{ flexShrink: 0 }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Stack direction="row" justifyContent="center" alignItems="center" flexWrap="wrap" sx={{ gap: 0.75 }}>
          {dotTargets.map((studentIdx, j) => {
            const isActive = j === activeDot
            return (
              <Box
                key={j}
                component="button"
                type="button"
                onClick={() => onSelectIndex(studentIdx)}
                sx={{
                  p: 0,
                  m: 0,
                  border: 'none',
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                  lineHeight: 0,
                }}
                aria-label={`Ученик ${studentIdx + 1}`}
              >
                <Box
                  sx={(theme) => ({
                    width: isActive ? 9 : 7,
                    height: isActive ? 9 : 7,
                    borderRadius: '50%',
                    bgcolor: isActive ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.35),
                    transition: 'width 0.15s, height 0.15s, background-color 0.15s',
                  })}
                />
              </Box>
            )
          })}
        </Stack>
        {n > PREVIEW_GROUP_MAX_DOTS ? (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
            До {PREVIEW_GROUP_MAX_DOTS} точек при большой группе (равномерный шаг по списку учеников)
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  )
}

function PreviewPanel(props: {
  title: string
  preview: PreviewState | null
  isLoading: boolean
  cardsLoading: boolean
  skillBadges: Skill[]
  autoCheckEnabled: boolean
  /** Когда превью намеренно не запрашивается (нет обязательных настроек слева). */
  selectionBlockHint: string | null
  /** Режим превью для теста на группу: карточки и метрики по выбранному ученику, навигатор сверху. */
  isGroupPreview: boolean
  participants: LessonParticipant[]
  groupStudentIndex: number
  onGroupStudentIndexChange: (index: number) => void
}) {
  const {
    title,
    preview,
    isLoading,
    cardsLoading,
    skillBadges,
    autoCheckEnabled,
    selectionBlockHint,
    isGroupPreview,
    participants,
    groupStudentIndex,
    onGroupStudentIndexChange,
  } = props

  const allUsers = useMemo(() => preview?.users ?? [], [preview?.users])

  const scopedUsers = useMemo(() => {
    if (!isGroupPreview || allUsers.length === 0) return allUsers
    const one = allUsers[groupStudentIndex]
    return one ? [one] : []
  }, [isGroupPreview, allUsers, groupStudentIndex])

  const allCards = useMemo<PreviewCardWithUser[]>(
    () => scopedUsers.flatMap((u) => u.cards.map((c) => ({ ...c, __user_id: u.user_id }))),
    [scopedUsers],
  )

  const relaxedCards = allCards.filter((c) => (c.relax_filters ?? []).length > 0)
  const exactCards = allCards.filter((c) => (c.relax_filters ?? []).length === 0)
  /** Одна карточка — один раз: послабление по фильтрам/тегам ИЛИ не попала в квотный «строгий» порог (например any_threshold ранее). */
  const extraCount = allCards.filter((c) => (c.relax_filters ?? []).length > 0 || !c.hit_threshold).length

  const { shortByQuota, thresholdBandRows } = useMemo(() => {
    const bandKey = (th: Pick<PreviewThreshold, 'name' | 'progress_from' | 'progress_to'>) =>
      `${(th.name ?? '').trim()}|${th.progress_from}|${th.progress_to}`
    const quotaByBand = new Map<string, number>()
    const metaByBand = new Map<
      string,
      { title: string; progress_from: number; progress_to: number; range: string }
    >()
    for (const u of scopedUsers) {
      for (const th of u.threshold ?? []) {
        const q = Math.max(0, Number(th.count_cards_in_test ?? 0))
        const key = bandKey(th)
        quotaByBand.set(key, (quotaByBand.get(key) ?? 0) + q)
        if (!metaByBand.has(key)) {
          const title = (th.name ?? '').trim() || 'Порог'
          metaByBand.set(key, {
            title,
            progress_from: th.progress_from,
            progress_to: th.progress_to,
            range: `${th.progress_from}–${th.progress_to}`,
          })
        }
      }
    }
    const strictByBand = new Map<string, number>()
    for (const c of allCards) {
      if (!c.hit_threshold) continue
      if ((c.relax_filters ?? []).length > 0) continue
      const tid = c.threshold_id
      if (!tid) continue
      const u = scopedUsers.find((x) => x.user_id === c.__user_id)
      const th = u?.threshold?.find((t) => t.id === tid)
      if (!th) continue
      const key = bandKey(th)
      strictByBand.set(key, (strictByBand.get(key) ?? 0) + 1)
    }
    let short = 0
    const rows: {
      key: string
      title: string
      range: string
      /** Заявленная квота карточек по порогу (сумма `count_cards_in_test` из API), не факт подбора. */
      declaredCardQuota: number
      /** Карточки строго в пороге: hit_threshold и без послаблений. */
      strictActual: number
      /** Отклонение от квоты: факт − норма (+ сверх квоты, − не добрано). */
      deviationFromQuota: number
      colors: { bg: string; border: string; chipBg: string }
    }[] = []
    for (const [key, declaredCardQuota] of quotaByBand) {
      const strictActual = strictByBand.get(key) ?? 0
      const deviationFromQuota = strictActual - declaredCardQuota
      short += Math.max(0, declaredCardQuota - strictActual)
      const meta = metaByBand.get(key)
      const title = meta?.title ?? 'Порог'
      const range = meta?.range ?? ''
      rows.push({
        key,
        title,
        range,
        declaredCardQuota,
        strictActual,
        deviationFromQuota,
        colors: pastelForKey(`${title}|${range}`),
      })
    }
    rows.sort((a, b) => {
      const af = metaByBand.get(a.key)?.progress_from ?? 0
      const bf = metaByBand.get(b.key)?.progress_from ?? 0
      if (af !== bf) return af - bf
      return a.title.localeCompare(b.title)
    })
    return { shortByQuota: short, thresholdBandRows: rows }
  }, [scopedUsers, allCards])
  /** Послабления / вне строгого порога + недобор по квотам полос (сколько «не дотянули» до плана по порогам). */
  const pickedExtraCount = extraCount + shortByQuota
  const hasShortage = Boolean(scopedUsers.some((u) => (u.shortage ?? 0) > 0))
  const hasRelaxed = pickedExtraCount > 0

  const totals = useMemo(() => {
    const need = scopedUsers.reduce((acc, u) => acc + (u.count_need ?? 0), 0)
    const found = scopedUsers.reduce((acc, u) => acc + (u.count_found ?? 0), 0)
    const shortage = scopedUsers.reduce((acc, u) => acc + Math.max(0, u.shortage ?? 0), 0)
    return { count_need: need, count_found: found, shortage }
  }, [scopedUsers])

  const thresholdMetaByUserAndId = useMemo(() => {
    const m = new Map<string, PreviewThreshold>()
    for (const u of scopedUsers) {
      for (const th of u.threshold ?? []) {
        if (typeof th?.id === 'number') m.set(`${u.user_id}:${th.id}`, th)
      }
    }
    return m
  }, [scopedUsers])

  const thresholdColorByCardKey = useMemo(() => {
    const out = new Map<string, { bg: string; border: string; chipBg: string }>()
    for (const c of allCards) {
      if (!c.hit_threshold) continue
      const tid = c.threshold_id
      if (!tid) continue
      const th = thresholdMetaByUserAndId.get(`${c.__user_id}:${tid}`)
      if (!th) continue
      const title = (th.name ?? '').trim() || 'Порог'
      const key = `${title}|${th.progress_from}–${th.progress_to}`
      out.set(`${c.__user_id}:${c.id}`, pastelForKey(key))
    }
    return out
  }, [allCards, thresholdMetaByUserAndId])

  return (
    <Paper variant="outlined" sx={{ p: 2, position: { md: 'sticky' }, top: { md: 16 } }}>
      {!isLoading && isGroupPreview && preview && allUsers.length > 0 ? (
        <GroupPreviewStudentStrip
          users={allUsers}
          participants={participants}
          selectedIndex={groupStudentIndex}
          onSelectIndex={onGroupStudentIndexChange}
        />
      ) : null}
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        {title}
      </Typography>

      {isLoading ? (
        <Stack spacing={0} sx={{ mb: 0 }}>
          <PreviewStatsSkeleton />
          <Box sx={{ maxHeight: 500, overflow: 'hidden' }}>
            <Stack spacing={0.75}>
              {Array.from({ length: 5 }, (_, i) => (
                <PreviewCardSkeleton key={i} />
              ))}
            </Stack>
          </Box>
        </Stack>
      ) : (
        <>
          {/* Статистика */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
              <Typography
                variant="h5"
                sx={(theme) => ({ fontFamily: 'monospace', lineHeight: 1.1, color: theme.palette.primary.main })}
              >
                {preview ? totals.count_need : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                слов в тесте
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: 1.25,
                flex: 1,
                bgcolor: hasRelaxed
                  ? alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.07 : 0.14)
                  : 'transparent',
              })}
            >
              <Typography
                variant="h5"
                sx={(theme) => ({
                  fontFamily: 'monospace',
                  lineHeight: 1.1,
                  color: hasRelaxed ? theme.palette.warning.dark : theme.palette.text.disabled,
                })}
              >
                {preview ? pickedExtraCount : '—'}
              </Typography>
              <Typography variant="caption" color={hasRelaxed ? 'warning.main' : 'text.disabled'}>
                добрано
              </Typography>
            </Paper>
          </Stack>

          {/* Список порогов, которые попали в карточки */}
          {thresholdBandRows.length > 0 ? (
            <Stack spacing={0.75} sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.8 }}>
                Пороги в подборе
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {thresholdBandRows.map((th) => {
                  const d = th.deviationFromQuota
                  const deviationSuffix = d === 0 ? '' : ` ${d > 0 ? '+' : ''}${d}`
                  const tip = `Квота по порогу (заявлено): ${th.declaredCardQuota}. Строго подобрано в порог: ${th.strictActual}. Отклонение: ${d === 0 ? '0' : `${d > 0 ? '+' : ''}${d}`}.`
                  return (
                    <Chip
                      key={th.key}
                      size="small"
                      title={tip}
                      label={`${th.title} · ${th.range} (${th.declaredCardQuota})${deviationSuffix}`}
                      variant="filled"
                      sx={{
                        backgroundColor: th.colors.bg,
                        backgroundImage: 'none',
                        borderColor: th.colors.border,
                        color: 'text.primary',
                        '& .MuiChip-label': { fontSize: '0.7rem' },
                      }}
                    />
                  )
                })}
              </Stack>
            </Stack>
          ) : null}

          {/* Алерт: добрано */}
          {hasRelaxed && preview ? (
            <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
              По фильтрам найдено {Math.max(0, totals.count_found - pickedExtraCount)} из {totals.count_need}. Добрано{' '}
              {pickedExtraCount} — с учётом послаблений, карточек вне строгого порога и недобора по квотам полос порогов.
            </Alert>
          ) : null}

          {/* Алерт: нехватка */}
          {hasShortage && preview ? (
            <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
              Не хватает {totals.shortage} карточек. Тест может быть неполным.
            </Alert>
          ) : null}

          {!preview ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
              {selectionBlockHint ??
                `После изменения параметров список обновится автоматически через ${PREVIEW_DEBOUNCE_MS / 1000} секунд.`}
            </Typography>
          ) : null}

          {/* Список карточек */}
          {(allCards.length > 0 || cardsLoading) && preview ? (
            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
              {cardsLoading ? (
                <Stack spacing={0.75}>
                  {Array.from({ length: Math.min(6, Math.max(allCards.length, 3)) }, (_, i) => (
                    <PreviewCardSkeleton key={i} />
                  ))}
                </Stack>
              ) : (
                <Stack spacing={0.75}>
                  {exactCards.length > 0 && (hasRelaxed || relaxedCards.length > 0) ? (
                    <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 2 }}>
                      По фильтрам ({exactCards.length})
                    </Typography>
                  ) : null}
                  {exactCards.map((c) => (
                    <CardBlock
                      key={`${c.__user_id}:${c.id}`}
                      card={c}
                      isRelaxed={false}
                      skillBadges={skillBadges}
                      autoCheckEnabled={autoCheckEnabled}
                      thresholdColors={thresholdColorByCardKey.get(`${c.__user_id}:${c.id}`) ?? null}
                    />
                  ))}

                  {relaxedCards.length > 0 ? (
                    <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 2, mt: 0.5 }}>
                      Добрано ({relaxedCards.length})
                    </Typography>
                  ) : null}
                  {relaxedCards.map((c) => (
                    <CardBlock
                      key={`${c.__user_id}:${c.id}`}
                      card={c}
                      isRelaxed={true}
                      skillBadges={skillBadges}
                      autoCheckEnabled={autoCheckEnabled}
                      thresholdColors={thresholdColorByCardKey.get(`${c.__user_id}:${c.id}`) ?? null}
                    />
                  ))}

                  {exactCards.length === 0 && relaxedCards.length === 0
                    ? allCards.map((c) => (
                        <CardBlock
                          key={`${c.__user_id}:${c.id}`}
                          card={c}
                          isRelaxed={false}
                          skillBadges={skillBadges}
                          autoCheckEnabled={autoCheckEnabled}
                          thresholdColors={thresholdColorByCardKey.get(`${c.__user_id}:${c.id}`) ?? null}
                        />
                      ))
                    : null}
                </Stack>
              )}
            </Box>
          ) : null}
        </>
      )}
    </Paper>
  )
}

export function LessonTestCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const authRoles = useAuthStore((s) => s.user?.roles ?? [])
  const canUseCustomTestTemplates = useAuthStore((s) => {
    const v = s.user?.subscription_parameters?.custom_test_templates_enabled
    return v !== 'false'
  })
  const { lessonId } = useParams()
  const id = useMemo(() => Number(lessonId), [lessonId])
  const isStudentRole = useMemo(() => authRoles.some((r) => r.slug === 'student'), [authRoles])

  const [lesson, setLesson] = useState<Lesson | null>(null)
  const [, setIsLessonLoading] = useState(false)
  const [participants, setParticipants] = useState<LessonParticipant[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [enumOptions, setEnumOptions] = useState<Record<string, EnumOption[]>>({})
  const wordAttributeKeys = useMemo(() => getWordAttributeFilterKeys(enumOptions), [enumOptions])
  const [templates, setTemplates] = useState<TestTemplate[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateId>('none')
  const isApplyingTemplateRef = useRef(false)
  const lastAppliedTemplateSignatureRef = useRef<string | null>(null)
  /** Пока false — не переводим выбор в «Без шаблона» при изменении полей (избегаем гонки с первичным apply «Базовый»). */
  const templateListBootstrapDoneRef = useRef(false)

  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false)
  const [saveTemplateTitle, setSaveTemplateTitle] = useState('')
  const [saveTemplateDescription, setSaveTemplateDescription] = useState('')
  const [isTemplateSaving, setIsTemplateSaving] = useState(false)

  const [targetUserId, setTargetUserId] = useState<number | ''>('')
  const [excludeDuplicateWords, setExcludeDuplicateWords] = useState(false)

  const [tagIds, setTagIds] = useState<number[]>([])
  const [relaxTagFilter, setRelaxTagFilter] = useState(true)
  const [explicitCardIds, setExplicitCardIds] = useState<number[]>([])
  const [explicitCardLabels, setExplicitCardLabels] = useState<Record<number, string>>({})
  const [explicitCardOptions, setExplicitCardOptions] = useState<Card[]>([])
  const [explicitCardInput, setExplicitCardInput] = useState('')
  const [explicitCardLoading, setExplicitCardLoading] = useState(false)
  const [explicitCardEnsureError, setExplicitCardEnsureError] = useState<string | null>(null)
  const [attributeConditions, setAttributeConditions] = useState<AttributeConditionRow[]>([])

  const [mode, setMode] = useState<Mode>('classic')
  const [countNeed, setCountNeed] = useState<number | ''>('')
  const [durationMin, setDurationMin] = useState<number | ''>('')
  const [seriesTarget, setSeriesTarget] = useState<number | ''>('')

  const [allowHints, setAllowHints] = useState(false)
  const [allowTranscription, setAllowTranscription] = useState(false)
  const [autoCheckEnabled, setAutoCheckEnabled] = useState(true)
  /** Режимы формата ответа в UI; при авто-проверке без отметок в API уходит пустой `enabled_answer_type`. */
  const [enabledAnswerFormatModes, setEnabledAnswerFormatModes] = useState<AnswerFormatMode[]>([])
  /** Пул типов отображения; для карточки бэкенд выбирает случайную допустимую пару с навыком. */
  const [enabledPromptTypes, setEnabledPromptTypes] = useState<PromptTypeKey[]>([PROMPT_SOURCE_TEXT])

  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [preview, setPreview] = useState<PreviewState | null>(null)
  const [previewCardsLoading, setPreviewCardsLoading] = useState(false)
  const [groupPreviewStudentIndex, setGroupPreviewStudentIndex] = useState(0)

  const previewUsersKey = useMemo(() => (preview?.users ?? []).map((u) => u.user_id).join(','), [preview?.users])

  useEffect(() => {
    const n = preview?.users?.length ?? 0
    if (n <= 0) {
      setGroupPreviewStudentIndex(0)
      return
    }
    setGroupPreviewStudentIndex((i) => Math.min(i, n - 1))
  }, [previewUsersKey, preview?.users?.length])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const previewTimerRef = useRef<number | null>(null)
  const hasInitialPreviewRef = useRef(false)

  const currentTemplateSignature = useMemo(() => {
    const sortedTagIds = [...tagIds].sort((a, b) => a - b)
    const sortedPrompts = [...enabledPromptTypes].slice().sort()
    const sortedModes = [...enabledAnswerFormatModes].slice().sort()
    const normalizedAttributeConds = attributeConditions
      .map((c) => ({
        field: c.field,
        operator: c.operator,
        value: (c.value ?? '').toString(),
      }))
      .sort((a, b) => {
        const ak = `${a.field}|${a.operator}|${a.value}`
        const bk = `${b.field}|${b.operator}|${b.value}`
        return ak.localeCompare(bk)
      })

    // Сигнатура должна отражать только поля, которые реально меняются при applyTemplate.
    return JSON.stringify({
      exclude_duplicate_words: Boolean(excludeDuplicateWords),
      mode,
      count_need: countNeed,
      duration_min: durationMin,
      series_target: seriesTarget,
      enabled_prompt_type: sortedPrompts,
      enabled_answer_format_modes: sortedModes,
      allow_hints: Boolean(allowHints),
      allow_transcription: Boolean(allowTranscription),
      auto_check_enabled: Boolean(autoCheckEnabled),
      filters: {
        tag_ids: sortedTagIds,
        relax_filter: Boolean(relaxTagFilter),
        attribute_conditions: normalizedAttributeConds,
      },
      explicit_card_ids: [...explicitCardIds].slice().sort((a, b) => a - b),
    })
  }, [
    excludeDuplicateWords,
    mode,
    countNeed,
    durationMin,
    seriesTarget,
    enabledPromptTypes,
    enabledAnswerFormatModes,
    allowHints,
    allowTranscription,
    autoCheckEnabled,
    tagIds,
    relaxTagFilter,
    explicitCardIds,
    attributeConditions,
  ])

  const togglePromptType = useCallback((p: PromptTypeKey) => {
    setEnabledPromptTypes((prev) => {
      if (prev.includes(p)) {
        if (prev.length <= 1) return prev
        return prev.filter((x) => x !== p)
      }
      return [...prev, p]
    })
  }, [])

  const applyTemplate = (tpl: TestTemplate) => {
    const s = (tpl.settings ?? {}) as Record<string, unknown>
    const completionFromTpl =
      s.completion_method && typeof s.completion_method === 'object'
        ? (s.completion_method as Record<string, unknown>)
        : null
    const helpFromTpl =
      s.help_settings && typeof s.help_settings === 'object' ? (s.help_settings as Record<string, unknown>) : null
    isApplyingTemplateRef.current = true
    setSelectedTemplate(tpl.id)

    // Фиксируем сигнатуру будущего состояния, чтобы не сбрасывать выбор шаблона
    // из эффекта «ручное изменение → Без шаблона».
    {
      let nextExcludeDuplicateWords = excludeDuplicateWords
      let nextMode = mode
      let nextCountNeed = countNeed
      let nextDurationMin = durationMin
      let nextSeriesTarget = seriesTarget
      let nextEnabledPromptTypes = enabledPromptTypes
      let nextEnabledAnswerFormatModes = enabledAnswerFormatModes
      let nextAllowHints = allowHints
      let nextAllowTranscription = allowTranscription
      let nextAutoCheckEnabled = autoCheckEnabled
      let nextTagIds = tagIds
      let nextRelaxTagFilter = relaxTagFilter
      let nextExplicitCardIds = explicitCardIds
      let nextAttributeConditions = attributeConditions

      if (typeof s.exclude_duplicate_words === 'boolean') nextExcludeDuplicateWords = Boolean(s.exclude_duplicate_words)

      const modeFromCm =
        completionFromTpl && typeof completionFromTpl.mode === 'string' ? (completionFromTpl.mode as string) : null
      if (modeFromCm === 'classic' || modeFromCm === 'timed' || modeFromCm === 'series') {
        nextMode = modeFromCm
      } else if (typeof s.mode === 'string' && (s.mode === 'classic' || s.mode === 'timed' || s.mode === 'series')) {
        nextMode = s.mode
      }

      const countFromTpl =
        typeof s.count_cards === 'number' && Number.isFinite(s.count_cards)
          ? (s.count_cards as number)
          : typeof s.count_need === 'number' && Number.isFinite(s.count_need)
            ? (s.count_need as number)
            : null
      if (countFromTpl !== null) nextCountNeed = countFromTpl

      const dmRaw =
        completionFromTpl && completionFromTpl.duration_min !== undefined && completionFromTpl.duration_min !== null
          ? completionFromTpl.duration_min
          : s.duration_min
      if (typeof dmRaw === 'number' && Number.isFinite(dmRaw)) nextDurationMin = dmRaw
      else if (typeof dmRaw === 'string' && dmRaw.trim() !== '' && Number.isFinite(Number(dmRaw))) {
        nextDurationMin = Number(dmRaw)
      }

      const stRaw =
        completionFromTpl && completionFromTpl.series_target !== undefined && completionFromTpl.series_target !== null
          ? completionFromTpl.series_target
          : s.series_target
      if (typeof stRaw === 'number' && Number.isFinite(stRaw)) nextSeriesTarget = stRaw
      else if (typeof stRaw === 'string' && stRaw.trim() !== '' && Number.isFinite(Number(stRaw))) {
        nextSeriesTarget = Number(stRaw)
      }

      const promptsRaw = Array.isArray(s.enabled_prompt_type) ? (s.enabled_prompt_type as string[]) : null
      const promptsFromTpl =
        promptsRaw?.filter((x): x is PromptTypeKey =>
          x === PROMPT_SOURCE_TEXT || x === PROMPT_TRANSLATION_TEXT || x === PROMPT_AUDIO,
        ) ?? null
      if (promptsFromTpl && promptsFromTpl.length > 0) {
        nextEnabledPromptTypes = promptsFromTpl
      } else if (typeof s.prompt_type === 'string') {
        const p = s.prompt_type.trim()
        if (p === PROMPT_SOURCE_TEXT || p === PROMPT_TRANSLATION_TEXT || p === PROMPT_AUDIO) {
          nextEnabledPromptTypes = [p]
        }
      }

      if (Array.isArray(s.enabled_answer_type)) {
        const granular = (s.enabled_answer_type as string[]).filter((x) => typeof x === 'string' && x.trim() !== '')
        nextEnabledAnswerFormatModes = answerFormatModesFromGranular(granular)
      } else if (typeof s.answer_type === 'string' && s.answer_type.trim() !== '') {
        nextEnabledAnswerFormatModes = answerFormatModesFromGranular([s.answer_type.trim()])
      } else {
        nextEnabledAnswerFormatModes = []
      }

      if (typeof helpFromTpl?.allow_hints === 'boolean') nextAllowHints = Boolean(helpFromTpl.allow_hints)
      else if (typeof s.allow_hints === 'boolean') nextAllowHints = Boolean(s.allow_hints)
      if (typeof helpFromTpl?.allow_transcription === 'boolean') {
        nextAllowTranscription = Boolean(helpFromTpl.allow_transcription)
      } else if (typeof s.allow_transcription === 'boolean') {
        nextAllowTranscription = Boolean(s.allow_transcription)
      }
      if (typeof s.auto_check_enabled === 'boolean') nextAutoCheckEnabled = Boolean(s.auto_check_enabled)

      const filters = (s.filters ?? null) as null | Record<string, unknown>
      if (filters && typeof filters === 'object') {
        const nextTagIdsRaw = Array.isArray(filters.tag_ids) ? (filters.tag_ids as number[]) : null
        if (nextTagIdsRaw) nextTagIds = nextTagIdsRaw.filter((x) => typeof x === 'number' && Number.isFinite(x))
        if (typeof filters.relax_filter === 'boolean') nextRelaxTagFilter = Boolean(filters.relax_filter)
        else if (typeof filters.relax_tag_filter === 'boolean') nextRelaxTagFilter = Boolean(filters.relax_tag_filter)
        const explicitRaw = Array.isArray((s as Record<string, unknown>).explicit_card_ids)
          ? ((s as Record<string, unknown>).explicit_card_ids as unknown[])
          : null
        if (explicitRaw) {
          nextExplicitCardIds = explicitRaw
            .map((x) => Number(x))
            .filter((x) => Number.isFinite(x) && x > 0)
        }
        const conds = Array.isArray(filters.attribute_conditions)
          ? (filters.attribute_conditions as Array<Record<string, unknown>>)
          : null
        if (conds) {
          const mapped: AttributeConditionRow[] = conds
            .map((c) => ({
              id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`,
              field: (c.field as WordAttributeFilterKey) ?? 'part_of_speech',
              operator: (c.operator as AttributeOperator) ?? 'eq',
              value: typeof c.value === 'string' ? c.value : '',
            }))
            .filter((c) => wordAttributeKeys.includes(c.field))
          nextAttributeConditions = mapped
        }
      }

      const sig = JSON.stringify({
        exclude_duplicate_words: Boolean(nextExcludeDuplicateWords),
        mode: nextMode,
        count_need: nextCountNeed,
        duration_min: nextDurationMin,
        series_target: nextSeriesTarget,
        enabled_prompt_type: [...nextEnabledPromptTypes].slice().sort(),
        enabled_answer_format_modes: [...nextEnabledAnswerFormatModes].slice().sort(),
        allow_hints: Boolean(nextAllowHints),
        allow_transcription: Boolean(nextAllowTranscription),
        auto_check_enabled: Boolean(nextAutoCheckEnabled),
        filters: {
          tag_ids: [...nextTagIds].slice().sort((a, b) => a - b),
          relax_filter: Boolean(nextRelaxTagFilter),
          attribute_conditions: nextAttributeConditions
            .map((c) => ({
              field: c.field,
              operator: c.operator,
              value: (c.value ?? '').toString(),
            }))
            .sort((a, b) => {
              const ak = `${a.field}|${a.operator}|${a.value}`
              const bk = `${b.field}|${b.operator}|${b.value}`
              return ak.localeCompare(bk)
            }),
        },
        explicit_card_ids: [...nextExplicitCardIds].slice().sort((a, b) => a - b),
      })
      lastAppliedTemplateSignatureRef.current = sig
    }

    // Расширенные настройки (если будут сохранены пользователем).
    if (typeof s.exclude_duplicate_words === 'boolean') setExcludeDuplicateWords(Boolean(s.exclude_duplicate_words))

    const modeFromCm2 =
      completionFromTpl && typeof completionFromTpl.mode === 'string' ? (completionFromTpl.mode as string) : null
    if (modeFromCm2 === 'classic' || modeFromCm2 === 'timed' || modeFromCm2 === 'series') {
      setMode(modeFromCm2)
    } else if (typeof s.mode === 'string' && (s.mode === 'classic' || s.mode === 'timed' || s.mode === 'series')) {
      setMode(s.mode)
    }

    const countFromTpl2 =
      typeof s.count_cards === 'number' && Number.isFinite(s.count_cards)
        ? (s.count_cards as number)
        : typeof s.count_need === 'number' && Number.isFinite(s.count_need)
          ? (s.count_need as number)
          : null
    if (countFromTpl2 !== null) setCountNeed(countFromTpl2)

    const dmRaw2 =
      completionFromTpl && completionFromTpl.duration_min !== undefined && completionFromTpl.duration_min !== null
        ? completionFromTpl.duration_min
        : s.duration_min
    if (typeof dmRaw2 === 'number' && Number.isFinite(dmRaw2)) setDurationMin(dmRaw2)
    else if (typeof dmRaw2 === 'string' && dmRaw2.trim() !== '' && Number.isFinite(Number(dmRaw2))) {
      setDurationMin(Number(dmRaw2))
    }

    const stRaw2 =
      completionFromTpl && completionFromTpl.series_target !== undefined && completionFromTpl.series_target !== null
        ? completionFromTpl.series_target
        : s.series_target
    if (typeof stRaw2 === 'number' && Number.isFinite(stRaw2)) setSeriesTarget(stRaw2)
    else if (typeof stRaw2 === 'string' && stRaw2.trim() !== '' && Number.isFinite(Number(stRaw2))) {
      setSeriesTarget(Number(stRaw2))
    }

    const promptsRaw = Array.isArray(s.enabled_prompt_type) ? (s.enabled_prompt_type as string[]) : null
    const promptsFromTpl =
      promptsRaw?.filter((x): x is PromptTypeKey =>
        x === PROMPT_SOURCE_TEXT || x === PROMPT_TRANSLATION_TEXT || x === PROMPT_AUDIO,
      ) ?? null
    if (promptsFromTpl && promptsFromTpl.length > 0) {
      setEnabledPromptTypes(promptsFromTpl)
    } else if (typeof s.prompt_type === 'string') {
      const p = s.prompt_type.trim()
      if (p === PROMPT_SOURCE_TEXT || p === PROMPT_TRANSLATION_TEXT || p === PROMPT_AUDIO) {
        setEnabledPromptTypes([p])
      }
    }

    if (Array.isArray(s.enabled_answer_type)) {
      const granular = (s.enabled_answer_type as string[]).filter((x) => typeof x === 'string' && x.trim() !== '')
      setEnabledAnswerFormatModes(answerFormatModesFromGranular(granular))
    } else if (typeof s.answer_type === 'string' && s.answer_type.trim() !== '') {
      setEnabledAnswerFormatModes(answerFormatModesFromGranular([s.answer_type.trim()]))
    } else {
      setEnabledAnswerFormatModes([])
    }

    if (typeof helpFromTpl?.allow_hints === 'boolean') setAllowHints(Boolean(helpFromTpl.allow_hints))
    else if (typeof s.allow_hints === 'boolean') setAllowHints(Boolean(s.allow_hints))
    if (typeof helpFromTpl?.allow_transcription === 'boolean') {
      setAllowTranscription(Boolean(helpFromTpl.allow_transcription))
    } else if (typeof s.allow_transcription === 'boolean') {
      setAllowTranscription(Boolean(s.allow_transcription))
    }
    if (typeof s.auto_check_enabled === 'boolean') setAutoCheckEnabled(Boolean(s.auto_check_enabled))

    const filters = (s.filters ?? null) as null | Record<string, unknown>
    if (filters && typeof filters === 'object') {
      const nextTagIds = Array.isArray(filters.tag_ids) ? (filters.tag_ids as number[]) : null
      if (nextTagIds) setTagIds(nextTagIds.filter((x) => typeof x === 'number' && Number.isFinite(x)))
      if (typeof filters.relax_filter === 'boolean') setRelaxTagFilter(Boolean(filters.relax_filter))
      else if (typeof filters.relax_tag_filter === 'boolean') setRelaxTagFilter(Boolean(filters.relax_tag_filter))
      const explicitRaw = Array.isArray((s as Record<string, unknown>).explicit_card_ids)
        ? ((s as Record<string, unknown>).explicit_card_ids as unknown[])
        : null
      if (explicitRaw) {
        setExplicitCardIds(
          explicitRaw.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0),
        )
      }
      const conds = Array.isArray(filters.attribute_conditions) ? (filters.attribute_conditions as Array<Record<string, unknown>>) : null
      if (conds) {
        const mapped: AttributeConditionRow[] = conds
          .map((c) => ({
            id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            field: (c.field as WordAttributeFilterKey) ?? 'part_of_speech',
            operator: (c.operator as AttributeOperator) ?? 'eq',
            value: typeof c.value === 'string' ? c.value : '',
          }))
          .filter((c) => wordAttributeKeys.includes(c.field))
        setAttributeConditions(mapped)
      }
    }

    // Сбрасываем после commit + useEffect, иначе эффект «ручное изменение → Без шаблона» успевает сработать раньше.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        isApplyingTemplateRef.current = false
      })
    })
  }

  useEffect(() => {
    if (!Number.isFinite(id)) return
    let isMounted = true
    setIsLessonLoading(true)
    lessonsApi
      .getById(id)
      .then((l) => {
        if (!isMounted) return
        setLesson(l)
      })
      .catch((e) => {
        if (!isMounted) return
        setError(getErrorMessage(e))
      })
      .finally(() => {
        if (!isMounted) return
        setIsLessonLoading(false)
      })

    lessonsApi
      .getParticipants(id)
      .then((ps) => {
        if (!isMounted) return
        setParticipants(ps)
        if (ps.length && targetUserId === '') setTargetUserId(ps[0].user_id)
      })
      .catch(() => {})

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    tagsApi.getList({ per_page: 100 }).then((r) => setTags(r.data)).catch(() => {})
    getEnums().then(setEnumOptions).catch(() => {})
  }, [])

  useEffect(() => {
    let cancelled = false
    setTemplatesLoading(true)
    testsApi
      .listTemplates()
      .then((list) => {
        if (cancelled) return
        const allowCustom =
          useAuthStore.getState().user?.subscription_parameters?.custom_test_templates_enabled !== 'false'
        setTemplates(allowCustom ? list : list.filter((x) => x.is_system))

        // По умолчанию: "Базовый" системный шаблон.
        const base = list.find((x) => x.slug === 'base') ?? list.find((x) => x.is_system) ?? null
        if (base && selectedTemplate === 'none') {
          applyTemplate(base)
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setTemplatesLoading(false)
          window.requestAnimationFrame(() => {
            templateListBootstrapDoneRef.current = true
          })
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const targetScope = useMemo<'student' | 'group'>(() => {
    return isLessonRecipientGroup(lesson?.recipient_type) ? 'group' : 'student'
  }, [lesson?.recipient_type])

  /** Согласовано с API preview: макс. 100; для группы >2 человек — ещё floor(200 / N) на ученика. */
  const previewCountNeedInputMax = useMemo(() => {
    if (targetScope !== 'group') return 100
    const n = participants.length
    if (n <= 2) return 100
    return Math.min(100, Math.floor(200 / n))
  }, [targetScope, participants.length])

  useEffect(() => {
    if (countNeed === '' || typeof countNeed !== 'number') return
    if (countNeed > previewCountNeedInputMax) {
      setCountNeed(previewCountNeedInputMax)
    }
  }, [previewCountNeedInputMax, countNeed])

  const resolvedTargetUserId = useMemo<number | null>(() => {
    if (targetScope !== 'student') return null
    if (typeof targetUserId === 'number' && Number.isFinite(targetUserId)) return targetUserId
    if (participants[0]?.user_id) return participants[0].user_id
    return isLessonRecipientUser(lesson?.recipient_type) ? Number(lesson?.recipient_id) : null
  }, [targetScope, targetUserId, participants, lesson])

  const previewRepresentativeUser = useMemo(() => {
    if (!preview?.users?.length) return null
    if (targetScope === 'group') {
      const n = preview.users.length
      const idx = Math.min(Math.max(0, groupPreviewStudentIndex), Math.max(0, n - 1))
      return preview.users[idx] ?? null
    }
    if (typeof resolvedTargetUserId === 'number' && Number.isFinite(resolvedTargetUserId)) {
      return preview.users.find((u) => u.user_id === resolvedTargetUserId) ?? preview.users[0] ?? null
    }
    return preview.users[0] ?? null
  }, [preview, targetScope, groupPreviewStudentIndex, resolvedTargetUserId])

  const previewCardsForCreate = useMemo(() => {
    const cards = previewRepresentativeUser?.cards
    if (!cards?.length) return undefined
    const rows = cards
      .filter((c) => typeof c.prompt_type === 'string' && c.prompt_type !== '' && typeof c.answer_type === 'string' && c.answer_type !== '')
      .map((c) => ({
        id: c.id,
        threshold_id: c.threshold_id,
        hit_threshold: Boolean(c.hit_threshold),
        prompt_type: c.prompt_type as string,
        answer_type: c.answer_type as string,
        primary_skill_type: (c.skill_type?.length ? c.skill_type[0] : null) ?? null,
        skill_type: normalizePreviewSkillTypes(c.skill_type),
      }))
    return rows.length > 0 ? rows : undefined
  }, [previewRepresentativeUser])

  /** Только id — иначе новый объект preview после каждого ответа пересобирает settings и зацикливает debounce-preview. */
  const previewUserIdForPreviewApi = previewRepresentativeUser?.user_id ?? null

  useEffect(() => {
    const q = explicitCardInput.trim()
    if (q.length < 2) {
      setExplicitCardOptions([])
      return
    }
    const timer = window.setTimeout(() => {
      setExplicitCardLoading(true)
      void cardsApi
        .getCards({ search: q, per_page: 25 })
        .then((res) => setExplicitCardOptions(res.data ?? []))
        .catch(() => setExplicitCardOptions([]))
        .finally(() => setExplicitCardLoading(false))
    }, 320)
    return () => window.clearTimeout(timer)
  }, [explicitCardInput])

  const handlePickExplicitTeacherCard = useCallback(
    async (_e: unknown, value: Card | null) => {
      setExplicitCardEnsureError(null)
      setExplicitCardInput('')
      if (!value) return
      if (targetScope === 'group') {
        setExplicitCardEnsureError('Для группы добавление по слову из словаря учителя пока недоступно.')
        return
      }
      if (typeof resolvedTargetUserId !== 'number' || !Number.isFinite(resolvedTargetUserId)) {
        setExplicitCardEnsureError('Выберите ученика')
        return
      }
      try {
        const sid = await ensureStudentCardForWordFromTeacherCard(value, resolvedTargetUserId)
        const label = (value.word?.text ?? '').trim() || `№${sid}`
        setExplicitCardIds((prev) => (prev.includes(sid) ? prev : [...prev, sid]))
        setExplicitCardLabels((prev) => ({ ...prev, [sid]: label }))
      } catch (e) {
        setExplicitCardEnsureError(getErrorMessage(e))
      }
    },
    [targetScope, resolvedTargetUserId],
  )

  const resolvedTargetLabel = useMemo(() => {
    if (targetScope === 'group') {
      return `Группа #${lesson?.recipient_id ?? '—'}`
    }
    const byParticipant = participants.find((p) => p.user_id === resolvedTargetUserId)
    if (byParticipant) return pickDisplayName(byParticipant)
    return resolvedTargetUserId ? `Ученик #${resolvedTargetUserId}` : '—'
  }, [targetScope, lesson, participants, resolvedTargetUserId])

  /** Участник для аватара в правой колонке (индивидуальный урок). */
  const lessonContextParticipant = useMemo(() => {
    if (targetScope === 'group') return null
    if (typeof resolvedTargetUserId === 'number' && Number.isFinite(resolvedTargetUserId)) {
      return participants.find((p) => p.user_id === resolvedTargetUserId) ?? null
    }
    return participants[0] ?? null
  }, [targetScope, resolvedTargetUserId, participants])

  const compatibleAnswerTypes = useMemo(
    () => unionAnswerTypesForPromptPool(enabledPromptTypes as string[], [...ALL_SKILL_TYPES]),
    [enabledPromptTypes],
  )

  const granularEnabledAnswerTypes = useMemo(() => {
    const fromModes = granularAnswerTypesFromModes(enabledAnswerFormatModes, compatibleAnswerTypes)
    if (!autoCheckEnabled) {
      const speechGranular = compatibleAnswerTypes.filter((at) => isSpeechAnswerType(at))
      if (enabledAnswerFormatModes.length === 0) {
        return speechGranular
      }
      return fromModes.filter((at) => isSpeechAnswerType(at))
    }
    return fromModes
  }, [autoCheckEnabled, enabledAnswerFormatModes, compatibleAnswerTypes])

  useEffect(() => {
    setEnabledAnswerFormatModes((prev) =>
      prev.filter((m) => modeHasCompatibleGranular(m, compatibleAnswerTypes)),
    )
  }, [compatibleAnswerTypes])

  useEffect(() => {
    if (!autoCheckEnabled) {
      setEnabledAnswerFormatModes((prev) => prev.filter((m) => m === 'speech'))
    }
  }, [autoCheckEnabled])

  const derivedActiveSkills = useMemo(
    () =>
      deriveActiveSkillsFromLessonTestSelection(
        enabledPromptTypes as string[],
        granularEnabledAnswerTypes,
      ) as Skill[],
    [enabledPromptTypes, granularEnabledAnswerTypes],
  )

  const getTemplateSettingsPayload = useCallback((): Record<string, unknown> => {
    const attributeConditionsFiltered = attributeConditions
      .filter((c) => c.operator === 'has' || c.operator === 'has_not' || c.value.trim() !== '')
      .map((c) => ({
        field: c.field,
        operator: c.operator,
        value: c.value.trim(),
      }))

    const completion_method =
      mode === 'series'
        ? { mode: 'series' as const, series_target: String(seriesTarget === '' ? 0 : seriesTarget) }
        : mode === 'timed'
          ? { mode: 'timed' as const, duration_min: String(durationMin === '' ? 0 : durationMin) }
          : { mode: 'classic' as const }

    return {
      exclude_duplicate_words: targetScope === 'group' ? Boolean(excludeDuplicateWords) : false,
      enabled_prompt_type: enabledPromptTypes,
      enabled_answer_type: granularEnabledAnswerTypes,
      auto_check_enabled: autoCheckEnabled,
      filters: {
        tag_ids: tagIds,
        relax_filter: relaxTagFilter,
        attribute_conditions: attributeConditionsFiltered,
      },
      completion_method,
      help_settings: {
        allow_hints: allowHints,
        allow_transcription: allowTranscription,
      },
      explicit_card_ids: [...explicitCardIds],
      ...(countNeed === '' ? {} : { count_cards: countNeed }),
    }
  }, [
    targetScope,
    excludeDuplicateWords,
    mode,
    countNeed,
    durationMin,
    seriesTarget,
    enabledPromptTypes,
    granularEnabledAnswerTypes,
    allowHints,
    allowTranscription,
    autoCheckEnabled,
    tagIds,
    relaxTagFilter,
    explicitCardIds,
    attributeConditions,
  ])

  const previewRequestPayload = useMemo((): TestPreviewRunRequestPayload | null => {
    if (targetScope === 'group') {
      if (!isLessonRecipientGroup(lesson?.recipient_type) || lesson?.recipient_id == null) return null
      if (!Number.isFinite(Number(lesson.recipient_id))) return null
    } else if (typeof resolvedTargetUserId !== 'number' || !Number.isFinite(resolvedTargetUserId)) {
      return null
    }

    const target_type = targetScope === 'group' ? 'group' : 'user'
    const target_id =
      target_type === 'group' ? Number(lesson!.recipient_id) : (resolvedTargetUserId as number)

    const attributeConditionsFiltered = attributeConditions
      .filter(
        (c) => c.operator === 'has' || c.operator === 'has_not' || c.value.trim() !== '',
      )
      .map((c) => ({
        field: c.field,
        operator: c.operator,
        value: c.value.trim(),
      }))

    const completion_method =
      mode === 'series'
        ? { mode: 'series' as const, series_target: String(seriesTarget === '' ? 0 : seriesTarget) }
        : mode === 'timed'
          ? { mode: 'timed' as const, duration_min: String(durationMin === '' ? 0 : durationMin) }
          : { mode: 'classic' as const }

    return {
      ...(Number.isFinite(id) && id > 0 ? { lesson_id: id } : {}),
      target_id,
      target_type,
      exclude_duplicate_words: targetScope === 'group' ? Boolean(excludeDuplicateWords) : false,
      enabled_prompt_type: enabledPromptTypes as string[],
      enabled_answer_type: granularEnabledAnswerTypes,
      auto_check_enabled: autoCheckEnabled,
      filters: {
        tag_ids: tagIds,
        relax_filter: relaxTagFilter,
        attribute_conditions: attributeConditionsFiltered,
      },
      completion_method,
      help_settings: {
        allow_hints: allowHints,
        allow_transcription: allowTranscription,
      },
      explicit_card_ids: [...explicitCardIds],
      ...(countNeed === '' ? {} : { count_cards: countNeed }),
    }
  }, [
    id,
    targetScope,
    lesson,
    resolvedTargetUserId,
    excludeDuplicateWords,
    enabledPromptTypes,
    granularEnabledAnswerTypes,
    autoCheckEnabled,
    tagIds,
    relaxTagFilter,
    attributeConditions,
    explicitCardIds,
    mode,
    seriesTarget,
    durationMin,
    countNeed,
    allowHints,
    allowTranscription,
  ])

  /** Тело `settings` для POST `/api/tests` — как превью + teacher-only поля снимка. */
  const lessonCreateSettingsPayload = useMemo(() => {
    if (!previewRequestPayload) return null
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { lesson_id, ...settingsBody } = previewRequestPayload
    return {
      ...settingsBody,
      ...(typeof previewUserIdForPreviewApi === 'number' && Number.isFinite(previewUserIdForPreviewApi)
        ? { preview_user_id: previewUserIdForPreviewApi }
        : {}),
      ...(previewCardsForCreate ? { preview_cards: previewCardsForCreate } : {}),
    }
  }, [previewRequestPayload, previewUserIdForPreviewApi, previewCardsForCreate])

  const handleTemplateChange = (tpl: TemplateId) => {
    if (tpl === 'none') {
      lastAppliedTemplateSignatureRef.current = null
      setSelectedTemplate('none')
      return
    }
    const found = templates.find((x) => x.id === tpl)
    if (found) applyTemplate(found)
  }

  useEffect(() => {
    if (!templateListBootstrapDoneRef.current) return
    if (isApplyingTemplateRef.current) return
    if (selectedTemplate === 'none') return
    // Если текущие настройки всё ещё совпадают с последним применённым шаблоном — не сбрасываем выбор.
    if (lastAppliedTemplateSignatureRef.current === currentTemplateSignature) return

    // Любое ручное изменение параметров — уходим в "Без шаблона".
    lastAppliedTemplateSignatureRef.current = null
    setSelectedTemplate('none')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTemplateSignature,
  ])

  const canPreview = useMemo(() => {
    if (targetScope === 'group') return true
    return typeof resolvedTargetUserId === 'number' && Number.isFinite(resolvedTargetUserId)
  }, [targetScope, resolvedTargetUserId])

  /** Без этих условий запрос превью не имеет смысла или даёт пустой/неконсистентный набор типов ответа. */
  const canRunPreview = useMemo(() => {
    if (!canPreview) return false
    if (!previewRequestPayload) return false
    if (enabledPromptTypes.length === 0) return false
    if (compatibleAnswerTypes.length === 0) return false
    if (autoCheckEnabled && enabledAnswerFormatModes.length === 0) return false
    return true
  }, [
    canPreview,
    previewRequestPayload,
    enabledPromptTypes.length,
    compatibleAnswerTypes.length,
    autoCheckEnabled,
    enabledAnswerFormatModes.length,
  ])

  const previewSelectionBlockHint = useMemo(() => {
    if (!canPreview || canRunPreview) return null
    return t('tests.createForm.previewNeedDisplayAndFormatHint', {
      display: t('tests.createForm.sectionDisplay'),
      answerFormat: t('tests.createForm.sectionAnswerFormat'),
    })
  }, [canPreview, canRunPreview, t])

  const handlePreview = async () => {
    if (!previewRequestPayload) return
    setError(null)
    setIsPreviewLoading(true)
    setPreviewCardsLoading(true)
    try {
      const res = await testsApi.preview(previewRequestPayload)
      const users = Array.isArray(res.users) ? res.users : []
      setPreview({
        users: users.map((u) => ({
          ...u,
          cards: (u.cards ?? []).map((c) => ({
            ...c,
            skill_type: normalizePreviewSkillTypes(c.skill_type),
          })),
        })),
      })
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setIsPreviewLoading(false)
      setPreviewCardsLoading(false)
    }
  }

  useEffect(() => {
    if (!canRunPreview) return
    if (hasInitialPreviewRef.current) return
    hasInitialPreviewRef.current = true
    void handlePreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRunPreview])

  useEffect(() => {
    if (!canRunPreview) return
    if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    previewTimerRef.current = window.setTimeout(() => {
      void handlePreview()
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRunPreview, previewRequestPayload, isStudentRole])

  useEffect(() => {
    if (canRunPreview) return
    setPreview(null)
    setIsPreviewLoading(false)
    setPreviewCardsLoading(false)
  }, [canRunPreview])

  // Карточки для предпросмотра приходят сразу в preview response.

  const handleSubmit = async () => {
    setError(null)
    setIsSubmitting(true)
    try {
      if (!lessonCreateSettingsPayload) {
        throw new Error('Недостаточно данных для создания теста')
      }
      const res = await testsApi.createFromLesson(id, lessonCreateSettingsPayload)
      navigate(ROUTES.TEST_RUN(res.id))
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <DashboardLayout>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to={ROUTES.DASHBOARD}
          underline="hover"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {t('dashboard.home')}
        </Link>
        <Link component={RouterLink} to={ROUTES.LESSONS} underline="hover" color="inherit">
          {t('lessons.title')}
        </Link>
        <Link component={RouterLink} to={ROUTES.LESSON_DETAILS(id)} underline="hover" color="inherit">
          {t('lessons.detailsTitle', { id })}
        </Link>
        <Typography color="text.primary">{t('tests.lessonCreateTitle')}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {t('tests.lessonCreateTitle')} · #{id}
        </Typography>
        <PageHelpPopover pageKey="lesson-tests-create" />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {/* content */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 360px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {/* main form */}
        <Box sx={{ minWidth: 0 }}>
          <Stack spacing={2}>
            <MuiCard id="template" sx={LESSON_TEST_SECTION_CARD_SX}>
              <CardContent sx={LESSON_TEST_SECTION_CARD_CONTENT_SX}>
                <Stack spacing={2}>
                  <TestCreateSectionHeader icon={<GridViewIcon fontSize="small" />} title="Шаблон" />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr' },
                      gap: 1,
                    }}
                  >
                    {templatesLoading
                      ? Array.from({ length: 4 }, (_, i) => (
                          <Paper key={i} variant="outlined" sx={{ p: 1.25 }}>
                            <Skeleton variant="text" width="70%" height={20} />
                            <Skeleton variant="text" width="90%" height={16} />
                          </Paper>
                        ))
                      : [
                          ...(templates ?? []),
                          {
                            id: 'none' as const,
                            slug: 'none',
                            title: 'Без шаблона',
                            description: 'настроить вручную',
                            settings: {},
                            is_system: true,
                            sort: Number.MAX_SAFE_INTEGER,
                          } satisfies Omit<TestTemplate, 'id'> & { id: 'none' },
                        ].map((tpl) => {
                          const selected = selectedTemplate === tpl.id
                          return (
                            <Paper
                              key={tpl.id}
                              variant="outlined"
                              sx={(theme) => ({
                                p: 1.25,
                                borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
                                bgcolor: selected
                                  ? alpha(
                                      theme.palette.primary.main,
                                      theme.palette.mode === 'light' ? 0.12 : 0.22,
                                    )
                                  : theme.palette.background.paper,
                                cursor: 'pointer',
                                transition: theme.transitions.create(['background-color', 'border-color'], {
                                  duration: theme.transitions.duration.shorter,
                                }),
                              })}
                              onClick={() => handleTemplateChange(tpl.id)}
                            >
                              <Typography
                                variant="body2"
                                sx={(theme) => ({
                                  fontWeight: 600,
                                  color: selected ? theme.palette.primary.main : theme.palette.text.primary,
                                })}
                              >
                                {tpl.title}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={(theme) => ({
                                  color: selected
                                    ? theme.palette.mode === 'light'
                                      ? theme.palette.primary.dark
                                      : theme.palette.primary.light
                                    : theme.palette.text.secondary,
                                })}
                              >
                                {tpl.description ?? ''}
                              </Typography>
                            </Paper>
                          )
                        })}
                  </Box>
                </Stack>
              </CardContent>
            </MuiCard>

            <MuiCard id="display" sx={LESSON_TEST_SECTION_CARD_SX}>
              <CardContent sx={LESSON_TEST_SECTION_CARD_CONTENT_SX}>
                <Stack spacing={2}>
                  <TestCreateSectionHeader
                    icon={<VisibilityIcon fontSize="small" />}
                    title={t('tests.createForm.sectionDisplay')}
                  />
                  <FormGroup>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                        gap: 1,
                      }}
                    >
                      {PROMPT_TYPE_OPTIONS.map((opt) => {
                        const checked = enabledPromptTypes.includes(opt.id)
                        const inputId = `lesson-test-prompt-${opt.id}`
                        return (
                          <Paper
                            key={opt.id}
                            variant="outlined"
                            sx={(theme) => ({
                              overflow: 'hidden',
                              borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
                              bgcolor: checked
                                ? alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === 'light' ? 0.12 : 0.22,
                                  )
                                : theme.palette.background.paper,
                              transition: theme.transitions.create(['background-color', 'border-color'], {
                                duration: theme.transitions.duration.shorter,
                              }),
                            })}
                          >
                            <Box
                              component="label"
                              htmlFor={inputId}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.25,
                                p: 1.25,
                                m: 0,
                                cursor: 'pointer',
                                userSelect: 'none',
                              }}
                            >
                              <Checkbox
                                id={inputId}
                                checked={checked}
                                onChange={() => togglePromptType(opt.id)}
                                color="primary"
                                sx={{
                                  p: 0,
                                  mt: 0.125,
                                  color: (theme) =>
                                    checked ? theme.palette.primary.main : theme.palette.action.active,
                                  '&.Mui-checked': {
                                    color: (theme) => theme.palette.primary.main,
                                  },
                                }}
                              />
                              <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, pt: 0.125 }}>
                                <Typography
                                  variant="body2"
                                  sx={(theme) => ({
                                    fontWeight: 600,
                                    color: checked
                                      ? theme.palette.mode === 'light'
                                        ? theme.palette.primary.dark
                                        : theme.palette.primary.light
                                      : theme.palette.text.primary,
                                  })}
                                >
                                  {t(`tests.run.promptTypes.${opt.id}`)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  component="span"
                                  sx={(theme) => ({
                                    display: 'block',
                                    lineHeight: 1.35,
                                    color: checked
                                      ? theme.palette.mode === 'light'
                                        ? alpha(theme.palette.primary.dark, 0.92)
                                        : alpha(theme.palette.primary.light, 0.9)
                                      : theme.palette.text.secondary,
                                  })}
                                >
                                  {t(`tests.createForm.promptTypeDescriptions.${opt.id}`, { defaultValue: '' })}
                                </Typography>
                              </Stack>
                            </Box>
                          </Paper>
                        )
                      })}
                    </Box>
                  </FormGroup>
                </Stack>
              </CardContent>
            </MuiCard>

            <MuiCard id="answer-format" sx={LESSON_TEST_SECTION_CARD_SX}>
              <CardContent sx={LESSON_TEST_SECTION_CARD_CONTENT_SX}>
                <Stack spacing={2}>
                  <TestCreateSectionHeader
                    icon={<ChatBubbleOutlineIcon fontSize="small" />}
                    title={t('tests.createForm.sectionAnswerFormat')}
                  />
                  {compatibleAnswerTypes.length > 0 ? (
                    <FormGroup>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, display: 'block' }}>
                        {t(
                          autoCheckEnabled
                            ? 'tests.createForm.answerFormatModesHintAutoOn'
                            : 'tests.createForm.answerFormatModesHintAutoOff',
                        )}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                          gap: 1,
                        }}
                      >
                        {ANSWER_FORMAT_MODES.map((mode) => {
                          const matrixAllows = modeHasCompatibleGranular(mode, compatibleAnswerTypes)
                          const available = autoCheckEnabled
                            ? matrixAllows
                            : mode === 'speech' && matrixAllows
                          const checked = enabledAnswerFormatModes.includes(mode)
                          const inputId = `lesson-test-answer-mode-${mode}`
                          const toggleMode = () => {
                            if (!available) return
                            setEnabledAnswerFormatModes((prev) =>
                              prev.includes(mode) ? prev.filter((x) => x !== mode) : [...prev, mode],
                            )
                          }
                          const card = (
                            <Paper
                              variant="outlined"
                              sx={(theme) => ({
                                overflow: 'hidden',
                                borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
                                bgcolor: checked
                                  ? alpha(
                                      theme.palette.primary.main,
                                      theme.palette.mode === 'light' ? 0.12 : 0.22,
                                    )
                                  : theme.palette.background.paper,
                                opacity: available ? 1 : 0.55,
                                transition: theme.transitions.create(['background-color', 'border-color', 'opacity'], {
                                  duration: theme.transitions.duration.shorter,
                                }),
                              })}
                            >
                              <Box
                                component="label"
                                htmlFor={inputId}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1.25,
                                  p: 1.25,
                                  m: 0,
                                  cursor: available ? 'pointer' : 'not-allowed',
                                  userSelect: 'none',
                                }}
                              >
                                <Checkbox
                                  id={inputId}
                                  checked={checked}
                                  disabled={!available}
                                  onChange={toggleMode}
                                  color="primary"
                                  sx={{
                                    p: 0,
                                    mt: 0.125,
                                    color: (theme) =>
                                      checked ? theme.palette.primary.main : theme.palette.action.active,
                                    '&.Mui-checked': {
                                      color: (theme) => theme.palette.primary.main,
                                    },
                                  }}
                                />
                                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, pt: 0.125 }}>
                                  <Typography
                                    variant="body2"
                                    sx={(theme) => ({
                                      fontWeight: 600,
                                      color: checked
                                        ? theme.palette.mode === 'light'
                                          ? theme.palette.primary.dark
                                          : theme.palette.primary.light
                                        : theme.palette.text.primary,
                                    })}
                                  >
                                    {t(`tests.createForm.answerFormatModes.${mode}.title`)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    component="span"
                                    sx={(theme) => ({
                                      display: 'block',
                                      lineHeight: 1.35,
                                      color: checked
                                        ? theme.palette.mode === 'light'
                                          ? alpha(theme.palette.primary.dark, 0.92)
                                          : alpha(theme.palette.primary.light, 0.9)
                                        : theme.palette.text.secondary,
                                    })}
                                  >
                                    {t(`tests.createForm.answerFormatModes.${mode}.description`)}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Paper>
                          )
                          const tooltipTitle =
                            !matrixAllows
                              ? t('tests.createForm.answerFormatModeUnavailable')
                              : !autoCheckEnabled && mode !== 'speech'
                                ? t('tests.createForm.answerFormatTypedChooseRequireAutoCheck')
                                : ''
                          return (
                            <Box key={mode} sx={{ width: '100%', minWidth: 0 }}>
                              {available ? (
                                card
                              ) : (
                                <Tooltip title={tooltipTitle} arrow>
                                  <Box component="span" sx={{ display: 'block', width: '100%' }}>
                                    {card}
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          )
                        })}
                      </Box>
                    </FormGroup>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('tests.form.answerFormatUnavailableHint')}
                    </Typography>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoCheckEnabled}
                        onChange={(e) => setAutoCheckEnabled(e.target.checked)}
                      />
                    }
                    label={t('tests.form.autoCheckEnabled')}
                  />
                </Stack>
              </CardContent>
            </MuiCard>

            <MuiCard id="skills" sx={LESSON_TEST_SECTION_CARD_SX}>
              <CardContent sx={LESSON_TEST_SECTION_CARD_CONTENT_SX}>
                <Stack spacing={2}>
                  <TestCreateSectionHeader
                    icon={<TrackChangesIcon fontSize="small" />}
                    title={t('tests.createForm.sectionCheckGoal')}
                  />
                  {derivedActiveSkills.length === 0 || compatibleAnswerTypes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {t('tests.createForm.noActiveSkillsNeedSelectionHint', {
                        display: t('tests.createForm.sectionDisplay'),
                        answerFormat: t('tests.createForm.sectionAnswerFormat'),
                      })}
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 1,
                      }}
                    >
                      {derivedActiveSkills.map((skillId) => {
                        const meta = SKILLS.find((s) => s.id === skillId)
                        if (!meta) return null
                        return (
                          <Paper
                            key={skillId}
                            variant="outlined"
                            sx={(theme) => ({
                              overflow: 'hidden',
                              borderColor: theme.palette.primary.main,
                              bgcolor: alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'light' ? 0.12 : 0.22,
                              ),
                            })}
                          >
                            <Stack spacing={0.25} sx={{ p: 1.25 }}>
                              <Typography
                                variant="body2"
                                sx={(theme) => ({
                                  fontWeight: 600,
                                  color:
                                    theme.palette.mode === 'light'
                                      ? theme.palette.primary.dark
                                      : theme.palette.primary.light,
                                })}
                              >
                                {meta.title}
                              </Typography>
                              <Typography
                                variant="caption"
                                component="span"
                                sx={(theme) => ({
                                  display: 'block',
                                  lineHeight: 1.35,
                                  color:
                                    theme.palette.mode === 'light'
                                      ? alpha(theme.palette.primary.dark, 0.92)
                                      : alpha(theme.palette.primary.light, 0.9),
                                })}
                              >
                                {meta.desc}
                              </Typography>
                            </Stack>
                          </Paper>
                        )
                      })}
                    </Box>
                  )}

                  {targetScope === 'group' && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={excludeDuplicateWords}
                          onChange={(e) => setExcludeDuplicateWords(e.target.checked)}
                        />
                      }
                      label="Исключить пересекающиеся слова (только для групп)"
                    />
                  )}
                </Stack>
              </CardContent>
            </MuiCard>

            <MuiCard id="word-filters" sx={LESSON_TEST_SECTION_CARD_SX}>
              <CardContent sx={LESSON_TEST_SECTION_CARD_CONTENT_SX}>
                <Stack spacing={3}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={(theme) => ({
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.22),
                        color: 'primary.main',
                      })}
                    >
                      <FilterAltIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Фильтры слов
                    </Typography>
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Теги
                    </Typography>
                    <Autocomplete
                      multiple
                      options={tags}
                      getOptionLabel={(o) => o.name}
                      value={tags.filter((tg) => tagIds.includes(tg.id))}
                      onChange={(_, value) => setTagIds(value.map((v) => v.id))}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} size="small" placeholder="добавить тег…" variant="outlined" />
                      )}
                    />
                  </Stack>

                  <Stack spacing={1.25}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Фильтрация по атрибутам слова
                    </Typography>
                    <Stack spacing={1}>
                      {attributeConditions.map((row) => {
                        const meta = ATTRIBUTE_META[row.field]
                        const ops = OPERATORS_BY_TYPE[meta.inputType]
                        const enumItems = meta.enumKey ? (enumOptions[meta.enumKey] ?? []) : []
                        const needsValue = row.operator !== 'has' && row.operator !== 'has_not'

                        return (
                          <Stack
                            key={row.id}
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                          >
                            {/* Атрибут */}
                            <TextField
                              select
                              size="small"
                              label="Атрибут"
                              value={row.field}
                              onChange={(e) => {
                                const v = e.target.value as WordAttributeFilterKey
                                if (!wordAttributeKeys.includes(v)) return
                                setAttributeConditions((prev) =>
                                  prev.map((r) =>
                                    r.id === row.id
                                      ? { ...r, field: v, operator: defaultOperator(v), value: '' }
                                      : r,
                                  ),
                                )
                              }}
                              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
                              sx={{ minWidth: 150, flexShrink: 0 }}
                            >
                              {wordAttributeKeys.map((key) => (
                                <MenuItem key={key} value={key}>
                                  {ATTRIBUTE_META[key as WordAttributeFilterKey].label}
                                </MenuItem>
                              ))}
                            </TextField>

                            {/* Оператор */}
                            <TextField
                              select
                              size="small"
                              label="Условие"
                              value={row.operator}
                              onChange={(e) =>
                                setAttributeConditions((prev) =>
                                  prev.map((r) =>
                                    r.id === row.id
                                      ? { ...r, operator: e.target.value as AttributeOperator, value: '' }
                                      : r,
                                  ),
                                )
                              }
                              sx={{ minWidth: meta.inputType === 'text' ? 145 : 110, flexShrink: 0 }}
                            >
                              {ops.map((op) => (
                                <MenuItem key={op.value} value={op.value}>
                                  {op.label}
                                </MenuItem>
                              ))}
                            </TextField>

                            {/* Значение */}
                            {needsValue && meta.inputType === 'enum' ? (
                              <TextField
                                select
                                size="small"
                                label="Значение"
                                value={row.value}
                                onChange={(e) =>
                                  setAttributeConditions((prev) =>
                                    prev.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)),
                                  )
                                }
                                sx={{ flex: 1, minWidth: 0 }}
                                SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}
                              >
                                {enumItems.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            ) : needsValue && meta.inputType === 'text' ? (
                              <TextField
                                size="small"
                                label="Значение"
                                value={row.value}
                                onChange={(e) =>
                                  setAttributeConditions((prev) =>
                                    prev.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)),
                                  )
                                }
                                placeholder="текст…"
                                sx={{ flex: 1, minWidth: 0 }}
                              />
                            ) : (
                              /* boolean — поле не нужно, растягиваем пространство */
                              <Box sx={{ flex: 1 }} />
                            )}

                            {/* Удалить */}
                            <IconButton
                              aria-label="Удалить условие"
                              size="small"
                              onClick={() =>
                                setAttributeConditions((prev) => prev.filter((r) => r.id !== row.id))
                              }
                              sx={{ flexShrink: 0 }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        )
                      })}
                      <Button
                        type="button"
                        size="small"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setAttributeConditions((prev) => [...prev, newAttributeConditionRow()])}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                      >
                        Добавить условие
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                      Условия объединяются через AND. Пример: часть речи ≠ gerund AND тип = phrase
                    </Typography>
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Конкретные карточки (опционально)
                    </Typography>
                    <Autocomplete
                      options={explicitCardOptions}
                      loading={explicitCardLoading}
                      inputValue={explicitCardInput}
                      onInputChange={(_, v) => setExplicitCardInput(v)}
                      onChange={handlePickExplicitTeacherCard}
                      getOptionLabel={(o) => (o.word?.text ?? '').trim() || `№${o.id}`}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      disabled={targetScope === 'group'}
                      filterOptions={(x) => x}
                      noOptionsText={explicitCardInput.trim().length < 2 ? 'Введите минимум 2 символа' : 'Нет карточек'}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="поиск по вашим словам, Enter — добавить в тест…"
                          fullWidth
                        />
                      )}
                    />
                    {explicitCardEnsureError ? (
                      <Alert severity="warning" sx={{ py: 0.5 }}>
                        {explicitCardEnsureError}
                      </Alert>
                    ) : null}
                    {explicitCardIds.length > 0 ? (
                      <Stack direction="row" flexWrap="wrap" gap={0.75}>
                        {explicitCardIds.map((cid) => (
                          <Chip
                            key={cid}
                            size="small"
                            label={explicitCardLabels[cid] ?? `№${cid}`}
                            onDelete={() => {
                              setExplicitCardIds((prev) => prev.filter((x) => x !== cid))
                              setExplicitCardLabels((prev) => {
                                const next = { ...prev }
                                delete next[cid]
                                return next
                              })
                            }}
                          />
                        ))}
                      </Stack>
                    ) : null}
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                      Карточки добавляются в тест первыми (вне порогов). Для индивидуального урока слово при необходимости
                      появляется у ученика из вашего словаря. Для группы список пока отключён.
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Постепенно послаблять фильтры при нехватке слов
                    </Typography>
                    <Switch
                      checked={relaxTagFilter}
                      onChange={(e) => setRelaxTagFilter(e.target.checked)}
                      color="primary"
                      sx={{ flexShrink: 0 }}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </MuiCard>

            <MuiCard id="mode" sx={LESSON_TEST_SECTION_CARD_SX}>
              <CardContent sx={LESSON_TEST_SECTION_CARD_CONTENT_SX}>
                <Stack spacing={2}>
                  <TestCreateSectionHeader
                    icon={<SportsScoreIcon fontSize="small" />}
                    title="Режим завершения теста"
                  />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      gap: 1,
                    }}
                  >
                    {MODES.map((m) => {
                      const selected = mode === m.id
                      return (
                        <Paper
                          key={m.id}
                          variant="outlined"
                          sx={(theme) => ({
                            p: 1.25,
                            borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
                            bgcolor: selected
                              ? alpha(
                                  theme.palette.primary.main,
                                  theme.palette.mode === 'light' ? 0.12 : 0.22,
                                )
                              : theme.palette.background.paper,
                            cursor: 'pointer',
                            transition: theme.transitions.create(['background-color', 'border-color'], {
                              duration: theme.transitions.duration.shorter,
                            }),
                          })}
                          onClick={() => setMode(m.id)}
                        >
                          <Typography
                            variant="body2"
                            sx={(theme) => ({
                              fontWeight: 600,
                              color: selected ? theme.palette.primary.main : theme.palette.text.primary,
                            })}
                          >
                            {m.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={(theme) => ({
                              color: selected
                                ? theme.palette.mode === 'light'
                                  ? theme.palette.primary.dark
                                  : theme.palette.primary.light
                                : theme.palette.text.secondary,
                            })}
                          >
                            {m.desc}
                          </Typography>
                        </Paper>
                      )
                    })}
                  </Box>
                  <TextField
                    size="small"
                    type="number"
                    label="Количество слов в тесте"
                    value={countNeed}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === '') {
                        setCountNeed('')
                        return
                      }
                      const num = Number(v)
                      if (!Number.isFinite(num)) return
                      const clamped = Math.min(
                        previewCountNeedInputMax,
                        Math.max(1, Math.trunc(num)),
                      )
                      setCountNeed(clamped)
                    }}
                    helperText={
                      targetScope === 'group' && participants.length > 2
                        ? `Максимум ${previewCountNeedInputMax} на ученика (не более 200 карточек на всю группу из ${participants.length} человек). Если пусто — из настроек ученика.`
                        : 'Не более 100. Если пусто — берётся из настроек ученика (thresholds)'
                    }
                    fullWidth
                    inputProps={{ min: 1, max: previewCountNeedInputMax }}
                  />
                  {mode === 'timed' ? (
                    <Stack spacing={1}>
                      <TextField
                        size="small"
                        type="number"
                        label="Ограничение времени (минут)"
                        value={durationMin}
                        onChange={(e) => {
                          const v = e.target.value
                          setDurationMin(v === '' ? '' : Number(v))
                        }}
                        fullWidth
                        inputProps={{ min: 0, max: 180 }}
                      />
                      <Alert severity="info" variant="outlined" sx={modeTestHintAlertSx}>
                        По истечению времени тест завершается автоматически. Если карточки заканчиваются раньше — время
                        фиксируется по завершению теста.
                      </Alert>
                    </Stack>
                  ) : null}
                  {mode === 'series' ? (
                    <Stack spacing={1}>
                      <TextField
                        size="small"
                        type="number"
                        label="Серия: N правильных подряд"
                        value={seriesTarget}
                        onChange={(e) => {
                          const v = e.target.value
                          setSeriesTarget(v === '' ? '' : Number(v))
                        }}
                        fullWidth
                        inputProps={{ min: 1, max: 50 }}
                      />
                      <Alert severity="info" variant="outlined" sx={modeTestHintAlertSx}>
                        Если слова закончились до серии — они идут по кругу.
                      </Alert>
                    </Stack>
                  ) : null}
                </Stack>
              </CardContent>
            </MuiCard>

            <MuiCard id="hints" sx={LESSON_TEST_SECTION_CARD_SX}>
              <CardContent sx={LESSON_TEST_SECTION_CARD_CONTENT_SX}>
                <Stack spacing={2}>
                  <TestCreateSectionHeader
                    icon={<LightbulbIcon fontSize="small" />}
                    title={t('tests.createForm.sectionHints')}
                  />
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <FormControlLabel
                      control={<Switch checked={allowHints} onChange={(e) => setAllowHints(e.target.checked)} />}
                      label={t('tests.form.allowHints')}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={allowTranscription}
                          onChange={(e) => setAllowTranscription(e.target.checked)}
                        />
                      }
                      label={t('tests.form.allowTranscription')}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </MuiCard>
          </Stack>
        </Box>

        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              {targetScope === 'group' ? (
                <Avatar
                  sx={(theme) => ({
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.22),
                    color: 'primary.main',
                  })}
                >
                  <GroupsIcon sx={{ fontSize: 22 }} />
                </Avatar>
              ) : (
                <Avatar
                  src={lessonContextParticipant?.user?.avatar_url ?? undefined}
                  sx={(theme) => ({
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.22),
                    color: 'primary.main',
                    fontWeight: 700,
                  })}
                >
                  {(lessonContextParticipant ? pickDisplayName(lessonContextParticipant) : resolvedTargetLabel).slice(0, 1).toUpperCase()}
                </Avatar>
              )}
              <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  <strong>Урок:</strong> #{id}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  <strong>Тип:</strong> {lesson?.lesson_scope ?? '—'}
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap title={resolvedTargetLabel}>
                  <strong>Кому:</strong> {resolvedTargetLabel}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="small"
            fullWidth
            sx={{ textTransform: 'none', height: 40 }}
            disabled={!canPreview || !canRunPreview || isSubmitting}
          >
            {isSubmitting ? t('common.saving') : t('tests.actions.create')}
          </Button>
          {selectedTemplate === 'none' && !isStudentRole && canUseCustomTestTemplates ? (
            <Button
              onClick={() => setIsSaveTemplateOpen(true)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{ textTransform: 'none', height: 40 }}
              disabled={!canPreview || !canRunPreview || isSubmitting}
            >
              Сохранить как шаблон
            </Button>
          ) : null}
          <PreviewPanel
            title="Предпросмотр карточек"
            preview={preview}
            isLoading={isPreviewLoading}
            cardsLoading={previewCardsLoading}
            skillBadges={derivedActiveSkills}
            autoCheckEnabled={autoCheckEnabled}
            selectionBlockHint={previewSelectionBlockHint}
            isGroupPreview={targetScope === 'group'}
            participants={participants}
            groupStudentIndex={groupPreviewStudentIndex}
            onGroupStudentIndexChange={setGroupPreviewStudentIndex}
          />
        </Stack>
      </Box>

      <Dialog
        open={isSaveTemplateOpen}
        onClose={() => {
          if (isTemplateSaving) return
          setIsSaveTemplateOpen(false)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Сохранить как шаблон</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <TextField
              label="Название"
              size="small"
              value={saveTemplateTitle}
              onChange={(e) => setSaveTemplateTitle(e.target.value)}
              autoFocus
              required
              fullWidth
            />
            <TextField
              label="Описание (опционально)"
              size="small"
              value={saveTemplateDescription}
              onChange={(e) => setSaveTemplateDescription(e.target.value)}
              fullWidth
            />
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              Будут сохранены текущие параметры теста (проверки, режим, фильтры, подсказки).
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsSaveTemplateOpen(false)}
            disabled={isTemplateSaving}
            sx={{ textTransform: 'none' }}
          >
            Отмена
          </Button>
          <Button
            onClick={async () => {
              const title = saveTemplateTitle.trim()
              if (title.length < 2) return
              setError(null)
              setIsTemplateSaving(true)
              try {
                const created = await testsApi.createTemplate({
                  title,
                  description: saveTemplateDescription.trim() ? saveTemplateDescription.trim() : null,
                  settings: getTemplateSettingsPayload(),
                })
                setTemplates((prev) => [...prev, created])
                setSelectedTemplate(created.id)
                setIsSaveTemplateOpen(false)
                setSaveTemplateTitle('')
                setSaveTemplateDescription('')
              } catch (e) {
                setError(getErrorMessage(e))
              } finally {
                setIsTemplateSaving(false)
              }
            }}
            variant="contained"
            disabled={isTemplateSaving || saveTemplateTitle.trim().length < 2}
            sx={{ textTransform: 'none' }}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  )
}

